import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { LayoutGrid, LayoutTemplate } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { ArtifactPanel, collectArtifacts } from "@/components/chat/Artifacts";
import { AttachButton, DropZone, PendingFiles } from "@/components/chat/Attachments";
import Composer, { ModelSelect, useDefaultModel, useModels } from "@/components/chat/Composer";
import MessageView, { plainText } from "@/components/chat/MessageView";
import { chatRenderers } from "@/components/chat/renderers";
import FolderPicker from "@/components/chat/FolderPicker";
import { ChatSpace } from "@/components/chat/ToolCall";
import { useFolders } from "@/lib/folders";
import ToolsPicker from "@/components/chat/ToolsPicker";
import { useVoiceChat, VoiceBar, VoiceButton } from "@/components/chat/Voice";
import { useStickToBottom } from "@/components/chat/useStickToBottom";
import { ErrorText, Spinner } from "@/components/ui";
import { contextLength, usedTokens } from "@/lib/context";
import { api } from "@/lib/api";
import { usePendingFiles } from "@/lib/files";
import { useLive, withLive } from "@/lib/live";
import { usePageTitle } from "@/lib/tabStatus";
import { buildTree, newestLeaf, pathTo, siblings } from "@/lib/tree";
import type { ChatDetail, Message, Part, SendResult } from "@/lib/types";

export function useChat(id: string | undefined) {
  return useQuery({
    queryKey: ["chat", id],
    queryFn: () => api.get<ChatDetail>(`/api/chats/${id}`),
    enabled: !!id,
  });
}

export function primeSendResult(qc: ReturnType<typeof useQueryClient>, r: SendResult) {
  qc.setQueryData<ChatDetail>(["chat", r.chat.id], (d) => {
    const msgs = [r.user_message, r.assistant_message].filter(Boolean) as Message[];
    if (!d) return { chat: r.chat, messages: msgs };
    const known = new Set(d.messages.map((m) => m.id));
    return { chat: r.chat, messages: [...d.messages, ...msgs.filter((m) => !known.has(m.id))] };
  });
  useLive.getState().seed(r.assistant_message);
}

export default function ChatPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const chat = useChat(id);
  const live = useLive((s) => s.live);
  const [model, setModel] = useState<string | null>(null);
  const defaultModel = useDefaultModel(chat.data?.chat.model_id);
  const modelId = model ?? defaultModel;
  const pending = usePendingFiles();
  usePageTitle(chat.data?.chat.title);
  const models = useModels();
  const folders = useFolders();
  const ctxLen = contextLength(models.data?.find((m) => m.id === modelId));

  useEffect(() => setModel(null), [id]);
  useEffect(() => {
    chat.data?.messages.forEach((m) => useLive.getState().seed(m));
  }, [chat.data]);

  const { tree, path } = useMemo(() => {
    const msgs = (chat.data?.messages ?? []).map((m) => withLive(m, live[m.id]));
    const tree = buildTree(msgs);
    return { tree, path: pathTo(tree, chat.data?.chat.current_leaf_id ?? null) };
  }, [chat.data, live]);

  const streamingMsg = path.find((m) => m.status === "streaming");
  const lastText = path.length ? JSON.stringify(path[path.length - 1].parts).length : 0;
  const { ref, toBottom } = useStickToBottom(`${path.length}:${lastText}`);

  const send = useMutation({
    mutationFn: (body: { content: string; parent_id?: string | null; parts?: Part[] }) =>
      api.post<SendResult>(`/api/chats/${id}/messages`, { ...body, model_id: modelId }),
    onSuccess: (r) => primeSendResult(qc, r),
  });
  const regenerate = useMutation({
    mutationFn: (messageId: string) => api.post<SendResult>(`/api/chats/${id}/messages/${messageId}/regenerate`, { model_id: modelId }),
    onSuccess: (r) => primeSendResult(qc, r),
  });
  const setTools = useMutation({
    mutationFn: (ids: string[]) => api.patch(`/api/chats/${id}`, { settings: { ...(chat.data?.chat.settings ?? {}), mcp_servers: ids } }),
  });
  const setFolder = useMutation({
    mutationFn: (folderId: string | null) => api.patch(`/api/chats/${id}`, { folder_id: folderId }),
  });
  const saveTemplate = useMutation({
    mutationFn: (name: string) =>
      api.post("/api/templates", {
        name,
        model_id: modelId,
        mcp_servers: (chat.data?.chat.settings.mcp_servers as string[] | undefined) ?? null,
        system_prompt: chat.data?.chat.system_prompt ?? null,
        folder_id: chat.data?.chat.folder_id ?? null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["templates"] }),
  });
  const setLeaf = useMutation({
    mutationFn: (leaf: string) => api.patch(`/api/chats/${id}`, { current_leaf_id: leaf }),
  });

  const lastMsg = path[path.length - 1];
  useVoiceChat(
    (text) => send.mutateAsync({ content: text }),
    () => {
      const s = path.find((m) => m.status === "streaming");
      if (s) void api.post(`/api/messages/${s.id}/stop`);
    },
    lastMsg?.role === "assistant" ? { id: lastMsg.id, text: plainText(lastMsg.parts), streaming: lastMsg.status === "streaming" } : null,
  );

  function switchBranch(m: Message, delta: number) {
    const sibs = siblings(tree, m);
    const target = sibs[sibs.indexOf(sibs.find((s) => s.id === m.id)!) + delta];
    if (!target) return;
    const leaf = newestLeaf(tree, target.id);
    qc.setQueryData<ChatDetail>(["chat", id], (d) => (d ? { ...d, chat: { ...d.chat, current_leaf_id: leaf } } : d));
    setLeaf.mutate(leaf);
  }

  if (chat.isLoading)
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size={24} />
      </div>
    );
  if (chat.error)
    return (
      <div className="flex flex-1 items-center justify-center">
        <ErrorText error={chat.error} />
      </div>
    );

  const busy = !!streamingMsg;
  const artifacts = collectArtifacts(path);
  const spaceId = chat.data?.chat.folder_id ?? null;
  const spaceName = folders.data?.find((f) => f.id === spaceId)?.name;
  return (
    <ChatSpace.Provider value={spaceId}>
    <div className="flex min-h-0 flex-1">
    <DropZone onFiles={pending.add} className="flex min-h-0 min-w-0 flex-1 flex-col">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-zinc-200 px-4 pl-14 md:pl-4 dark:border-zinc-800">
        <h1 className="min-w-0 flex-1 truncate text-sm font-medium">{chat.data?.chat.title}</h1>
        {spaceId && spaceName && (
          <Link to={`/spaces/${spaceId}`} className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-950" title="Open the space">
            <LayoutGrid size={14} /> <span className="max-w-[10rem] truncate">{spaceName}</span>
          </Link>
        )}
        <button
          type="button"
          onClick={() => {
            const name = window.prompt("Name for the template (model, tools, system prompt and folder of this chat):", chat.data?.chat.title);
            if (name?.trim()) saveTemplate.mutate(name.trim());
          }}
          className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          title="Save this chat's model, tools, system prompt and folder as a template"
        >
          <LayoutTemplate size={14} /> {saveTemplate.isSuccess ? "Saved" : "Save as template"}
        </button>
        {saveTemplate.error && <span className="text-xs text-red-600">{(saveTemplate.error as Error).message}</span>}
      </header>
      <div ref={ref} className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl space-y-6 px-4 py-6">
          {path.map((m) => {
            const sibs = siblings(tree, m);
            return (
              <MessageView
                key={m.id}
                message={m}
                busy={busy}
                renderers={chatRenderers}
                branch={{ index: sibs.findIndex((s) => s.id === m.id), count: sibs.length, go: (d) => switchBranch(m, d) }}
                onEdit={m.role === "user" ? (text) => text.trim() && send.mutate({ content: text, parent_id: m.parent_id }) : undefined}
                onRegenerate={m.role === "assistant" ? () => regenerate.mutate(m.id) : undefined}
              />
            );
          })}
        </div>
      </div>
      <div className="mx-auto w-full max-w-3xl px-4 pb-4">
        <ErrorText error={send.error || regenerate.error} />
        <VoiceBar />
        <Composer
          streaming={busy}
          onStop={() =>
            streamingMsg &&
            api.post(`/api/messages/${streamingMsg.id}/stop`).catch(() => {
              // Nothing running any more (finished, or lost in a server restart): reload the chat.
              if (streamingMsg) useLive.getState().forget(streamingMsg.id);
              qc.invalidateQueries({ queryKey: ["chat", id] });
            })
          }
          onSend={async (text) => {
            toBottom();
            try {
              await send.mutateAsync({ content: text, parts: pending.parts });
              pending.clear();
            } catch {
              return false;
            }
          }}
          disabled={pending.uploading}
          context={ctxLen ? { used: usedTokens(path), length: ctxLen } : null}
          canSendEmpty={pending.parts.length > 0}
          onPasteFiles={pending.add}
          attachments={<PendingFiles files={pending.files} onRemove={pending.remove} />}
          toolbar={
            <>
              <AttachButton onFiles={pending.add} />
              <VoiceButton />
              <ToolsPicker value={chat.data?.chat.settings.mcp_servers as string[] | undefined} onChange={(ids) => setTools.mutate(ids)} />
              <FolderPicker value={chat.data?.chat.folder_id} onChange={(f) => setFolder.mutate(f)} />
              <ModelSelect value={modelId} onChange={setModel} />
            </>
          }
        />
      </div>
    </DropZone>
    <ArtifactPanel all={artifacts} />
    </div>
    </ChatSpace.Provider>
  );
}
